import Link from 'next/link';
import Navbar from './common/Navbar';
import { useSession } from 'next-auth/react';
import { useState, useEffect, useRef } from 'react';
import LoginForm from './auth/LoginForm';
import SignUp from './auth/SignUp';
import AdminMenu from './menu/AdminMenu';
import UserMenu from './menu/UserMenu';
import { useCart } from '@context/CartContext';

export default function Header() {
  const { data: session } = useSession();
  const { cart, removeFromCart, showCart, setShowCart } = useCart();
  const [showAuthDropdown, setShowAuthDropdown] = useState(false);
  const [showLogin, setShowLogin] = useState(true);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const dropdownRef = useRef(null);
  const cartDropdownRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleAuthDropdown = () => {
    setShowAuthDropdown(prev => !prev);
    setShowCart(false);
  };

  const toggleCartDropdown = () => {
    setShowCart(prev => !prev);
    setShowAuthDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAuthDropdown(false);
      }
      if (cartDropdownRef.current && !cartDropdownRef.current.contains(event.target)) {
        setShowCart(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Prevent background scrolling when sidebars are open
  useEffect(() => {
    if (showAuthDropdown || showCart || isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showAuthDropdown, showCart, isMobileMenuOpen]);

  const role = session?.user?.role;

  return (
    <>
      {/* Modern Header - No Top Bar */}
      <header
        className={`top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'fixed bg-white/95 backdrop-blur-md shadow-lg py-3'
          : 'relative bg-white py-4'
          }`}
      >
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-[auto_1fr_auto_auto] lg:flex lg:items-center lg:justify-between items-center gap-2 sm:gap-3">
            <button
              className="relative p-2.5 rounded-md hover:bg-brand-primary hover:text-white transition-all duration-300 focus:outline-none group justify-self-start lg:order-3"
              onClick={toggleCartDropdown}
              aria-label="Open cart"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-brand-dark group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                  {cart.length}
                </span>
              )}
            </button>

            {/* Logo */}
            <div className="flex-shrink-0 justify-self-center lg:justify-self-auto lg:order-1">
              <Link href="/" className="flex items-center gap-2">
                <img
                  src="/assets/img/logo.png"
                  alt="L.I.S.T Learning Pathways"
                  className={`h-12 sm:h-14 lg:h-14 transition-all duration-300 ${isScrolled ? 'scale-95' : 'scale-100'}`}
                />
              </Link>
            </div>

            {/* Navigation - Main Menu */}
            <div className="hidden lg:block lg:ml-auto lg:mr-6 lg:order-2">
              <Navbar
                role={session?.user?.role || 'user'}
                onLinkClick={toggleAuthDropdown}
              />
            </div>

            {/* Right Icons & Actions */}
            <div className="flex items-center gap-2 sm:gap-3 justify-self-end lg:justify-self-auto lg:order-4">
              {!session ? (
                <div className="hidden lg:flex items-center gap-3">
                  <Link
                    href="/login"
                    className="px-5 py-2.5 text-sm font-semibold text-brand-dark hover:text-brand-primary transition-colors rounded-md hover:bg-brand-primary/5"
                  >
                    Log In
                  </Link>
                  <Link
                    href="/enrollment"
                    className="px-6 py-2.5 bg-brand-primary text-white text-sm font-bold rounded-md hover:shadow-xl hover:shadow-brand-primary/30 transition-all transform hover:-translate-y-0.5"
                  >
                    Enroll Now
                  </Link>
                </div>
              ) : (
                <div className="relative group">
                  <button className="flex items-center gap-3 p-1 hover:bg-gray-50 rounded-lg transition-all focus:outline-none">
                    <img
                      src={session?.user?.profilePic || session?.user?.image || "/assets/img/default-profile.png"}
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-md border-2 border-brand-primary/20 hover:border-brand-primary hover:shadow-lg hover:shadow-brand-primary/20 transition-all object-cover"
                      alt="User"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/assets/img/default-profile.png";
                      }}
                    />
                    <div className="hidden lg:flex flex-col items-start leading-tight">
                      <span className="text-sm font-bold text-brand-dark truncate max-w-[120px]">
                        {session?.user?.name || 'User'}
                      </span>
                      <span className="text-[10px] font-bold text-brand-primary uppercase tracking-wider">
                        {session?.user?.role || 'User'}
                      </span>
                    </div>
                  </button>
                  {/* User Dropdown - Common for both mobile/desktop trigger */}
                  <div className="absolute right-0 top-full mt-3 w-64 bg-white rounded-lg shadow-2xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-right overflow-hidden z-50">
                    <div className="p-5 bg-brand-primary/5 border-b border-gray-100">
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={session?.user?.profilePic || session?.user?.image || "/assets/img/default-profile.png"}
                          className="w-12 h-12 rounded-lg object-cover border-2 border-white shadow-sm"
                          alt="Profile"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "/assets/img/default-profile.png";
                          }}
                        />
                        <div>
                          <p className="font-bold text-brand-dark text-base leading-none mb-1">{session?.user?.name || 'User'}</p>
                          <p className="text-[10px] font-bold text-brand-primary uppercase tracking-tighter">{session?.user?.role || 'User'}</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 truncate">{session?.user?.email}</p>
                    </div>
                    <div className="p-2">
                      {role?.toLowerCase() === 'admin' ? <AdminMenu onLinkClick={() => setIsMobileMenuOpen(false)} /> : <UserMenu onLinkClick={() => setIsMobileMenuOpen(false)} />}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden justify-self-end">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2.5 rounded-md hover:bg-brand-primary hover:text-white transition-all duration-300 focus:outline-none"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 animate-in slide-in-from-top-2 duration-300 bg-white/95 backdrop-blur-md">
            <div className="p-5 space-y-4">
              <Navbar
                role={session?.user?.role || 'user'}
                onLinkClick={() => setIsMobileMenuOpen(false)}
                isMobile
              />
              {!session && (
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  <Link
                    href="/login"
                    className="block w-full py-3 text-center border-2 border-brand-dark text-brand-dark font-bold rounded-md hover:bg-brand-dark hover:text-white transition-all"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Log In
                  </Link>
                  <Link
                    href="/enrollment"
                    className="block w-full py-3 text-center bg-brand-primary text-white font-bold rounded-md hover:shadow-xl transition-all"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Enroll Now
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Cart Sidebar & Backdrop */}
      {showCart && (
        <div
          className="fixed inset-0 bg-brand-dark/50 backdrop-blur-sm z-[100] transition-opacity duration-300 animate-in fade-in"
          onClick={toggleCartDropdown}
        />
      )}
      <div
        ref={cartDropdownRef}
        className={`fixed inset-y-0 right-0 w-[420px] max-w-[90vw] bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-[110] overflow-hidden flex flex-col ${showCart ? 'translate-x-0' : 'translate-x-full invisible'} rounded-l-3xl`}
        style={{ backgroundColor: '#ffffff' }}
      >
        <div className="p-6 flex justify-between items-center border-b bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-brand-primary flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <h3 className="font-bold text-xl text-brand-dark">Your Cart</h3>
          </div>
          <button
            onClick={toggleCartDropdown}
            className="p-2 rounded-md hover:bg-gray-100 transition-all"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {cart.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {cart.map(item => (
                <div key={item.cartItemId} className="flex items-center p-6 hover:bg-gray-50/50 space-x-4 group">
                  <div className="flex-1">
                    <h4 className="font-bold text-brand-dark line-clamp-2 text-sm">{item.title}</h4>
                    <p className="text-brand-primary font-bold mt-2">₹ {item.price}</p>
                    <p className="text-[10px] text-gray-400 font-medium">(Inclusive of 18% GST)</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.cartItemId, item.id)}
                    className="p-2.5 text-gray-300 hover:bg-red-50 hover:text-red-500 transition-all rounded-md"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-8 text-center">
              <div className="w-48 h-48 mb-8 opacity-20">
                <svg className="w-full h-full text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-brand-dark mb-3">Your cart is empty</h4>
              <p className="text-gray-500 mb-8 text-sm">Looks like you haven't added any programs yet.</p>
              <button
                onClick={toggleCartDropdown}
                className="bg-brand-primary text-white px-8 py-4 rounded-lg font-bold hover:shadow-xl transition-all"
              >
                Browse Programs
              </button>
            </div>
          )}
        </div>

        {cart.length > 0 && (
          <div className="p-6 border-t bg-white">
            <div className="flex justify-between items-center mb-6">
              <span className="text-gray-600 font-medium">Subtotal</span>
              <span className="text-3xl font-bold text-brand-dark">
                ₹ {cart.reduce((total, item) => total + item.price, 0)}
              </span>
              <p className="text-[10px] text-gray-400 font-medium text-right mt-1">(Inclusive of 18% GST)</p>
            </div>
            <Link
              href="/checkout"
              onClick={toggleCartDropdown}
              className="block w-full py-4 bg-brand-primary text-white text-center font-bold rounded-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
            >
              Proceed to Checkout
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
